import { useCallback, useMemo, useState } from "react";

import { Loader, Menu } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconChevronDown } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { UserService } from "services";
import { AppUtils } from "utils";

import Text from "../text";

import classes from "./LanguageSwitcher.module.css";

const SUPPORTED_LANGUAGES = ["DE", "EN", "SR"] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

function getResolvedLanguage(language?: string): SupportedLanguage {
  const candidate = language?.split("-")[0].toUpperCase() as SupportedLanguage | undefined;

  if (candidate && SUPPORTED_LANGUAGES.includes(candidate)) {
    return candidate;
  }

  return "DE";
}

const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);

  const currentLanguage = useMemo(() => getResolvedLanguage(i18n.language), [i18n.language]);

  const handleLanguageChange = useCallback(
    async (language: SupportedLanguage) => {
      if (loading || language === currentLanguage) {
        return;
      }

      try {
        setLoading(true);
        const nextLanguage = await UserService.changeLanguage(language);
        AppUtils.changeLanguage(nextLanguage);
      } catch {
        notifications.show({
          color: "red",
          title: t("languageSwitcher.error.title"),
          message: t("languageSwitcher.error.message"),
        });
      } finally {
        setLoading(false);
      }
    },
    [currentLanguage, loading, t],
  );

  return (
    <Menu shadow={"md"} width={180} position={"bottom-end"} withArrow={true}>
      <Menu.Target>
        <button
          aria-label={t("languageSwitcher.label")}
          className={classes.switcherButton}
          disabled={loading}
          type={"button"}
        >
          {loading ? (
            <Loader color={"var(--mantine-color-white)"} size={14} />
          ) : (
            <Text c={"var(--mantine-color-white)"} className={classes.switcherCode}>
              {currentLanguage}
            </Text>
          )}
          <IconChevronDown color={"currentColor"} size={14} stroke={1.75} />
        </button>
      </Menu.Target>
      <Menu.Dropdown>
        {SUPPORTED_LANGUAGES.map((language) => (
          <Menu.Item
            disabled={loading || language === currentLanguage}
            key={language}
            onClick={() => handleLanguageChange(language)}
          >
            {t(`user.language.options.${language}`)}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};

export default LanguageSwitcher;
